import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit2, Trash2, Save, X, Eye, LogOut, Package, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  getProducts, saveProducts, addProduct, updateProduct, deleteProduct,
  COLLECTIONS_LIST, PRODUCT_TYPES, SIZES_LIST, COLORS_LIST, OCCASIONS_LIST,
} from '../data/products';

// Simple password — change this to something secure
const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD;
const AUTH_KEY = 'aura_admin_auth';

const EMPTY_FORM = {
  name: '', collection: 'pet-anime', type: 'tshirt', occasion: [],
  price: '', originalPrice: '', colors: [], sizes: [], images: ['', ''],
  description: '', badge: '', inStock: true, customisable: true, tags: '',
};

const AdminPage = () => {
  const [authed, setAuthed] = useState(() => sessionStorage.getItem(AUTH_KEY) === 'yes');
  const [passwordInput, setPasswordInput] = useState('');
  const [pwError, setPwError] = useState('');
  const [products, setProducts] = useState([]);
  const [editingId, setEditingId] = useState(null); // null = add new
  const [form, setForm] = useState(EMPTY_FORM);
  const [formOpen, setFormOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const formRef = useRef(null);

  useEffect(() => {
    if (authed) setProducts(getProducts());
  }, [authed]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (passwordInput === ADMIN_PASSWORD) {
      sessionStorage.setItem(AUTH_KEY, 'yes');
      setAuthed(true);
    } else {
      setPwError('Incorrect password. Try again.');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(AUTH_KEY);
    setAuthed(false);
  };

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setFormOpen(true);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const openEdit = (product) => {
    setForm({
      ...product,
      originalPrice: product.originalPrice || '',
      tags: (product.tags || []).join(', '),
      images: product.images.length >= 2 ? product.images : [...product.images, ''],
    });
    setEditingId(product.id);
    setFormOpen(true);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const handleSave = () => {
    if (!form.name || !form.price) {
      alert('Name and Price are required.');
      return;
    }
    const payload = {
      ...form,
      price: Number(form.price),
      originalPrice: form.originalPrice ? Number(form.originalPrice) : null,
      images: form.images.filter(Boolean),
      tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
    };
    if (editingId) {
      updateProduct(editingId, payload);
    } else {
      addProduct(payload);
    }
    setProducts(getProducts());
    setFormOpen(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleDelete = (id) => {
    deleteProduct(id);
    setProducts(getProducts());
    setDeleteConfirm(null);
  };

  const toggleArray = (key, value) => {
    setForm(prev => {
      const arr = prev[key] || [];
      return { ...prev, [key]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value] };
    });
  };

  const toggleColor = (color) => {
    setForm(prev => {
      const exists = (prev.colors || []).some(c => c.name === color.name);
      return {
        ...prev,
        colors: exists
          ? prev.colors.filter(c => c.name !== color.name)
          : [...(prev.colors || []), color],
      };
    });
  };

  // ─── Login Screen ────────────────────────────────────────────────────────────
  if (!authed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[hsl(var(--cream))] to-[hsl(var(--peach))] flex items-center justify-center px-4">
        <div className="bg-white rounded-3xl shadow-soft-lg p-8 w-full max-w-sm">
          <div className="text-center mb-6">
            <div className="w-14 h-14 rounded-2xl bg-[hsl(350,70%,65%)]/10 flex items-center justify-center mx-auto mb-4">
              <ShieldCheck className="w-7 h-7 text-[hsl(350,70%,55%)]" />
            </div>
            <h1 className="text-xl font-bold text-gray-900">Admin Panel</h1>
            <p className="text-sm text-gray-500 mt-1">AuraPrintiqo Product Manager</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1.5">Password</label>
              <input
                type="password"
                value={passwordInput}
                onChange={e => { setPasswordInput(e.target.value); setPwError(''); }}
                placeholder="Enter admin password"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[hsl(350,70%,65%)]/30 focus:border-[hsl(350,70%,65%)] text-sm"
              />
              {pwError && <p className="text-red-500 text-xs mt-1.5">{pwError}</p>}
            </div>
            <button type="submit"
              className="w-full py-3 rounded-xl text-white font-semibold text-sm"
              style={{ background: 'linear-gradient(135deg,hsl(350,70%,65%),hsl(20,90%,70%))' }}>
              Login
            </button>
          </form>
          <p className="text-center text-xs text-gray-400 mt-4">
            <Link to="/" className="hover:text-gray-600">← Back to website</Link>
          </p>
        </div>
      </div>
    );
  }

  // ─── Admin Panel ─────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Package className="w-5 h-5 text-[hsl(350,70%,55%)]" />
            <h1 className="font-bold text-gray-900">Product Manager</h1>
            <span className="px-2 py-0.5 bg-gray-100 rounded-full text-xs text-gray-500">{products.length} products</span>
          </div>
          <div className="flex items-center gap-3">
            {saved && (
              <span className="flex items-center gap-1 text-green-600 text-sm font-medium">
                <Save className="w-4 h-4" /> Saved!
              </span>
            )}
            <Link to="/shop" target="_blank"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-600 hover:border-gray-300 transition-colors">
              <Eye className="w-4 h-4" /> View Shop
            </Link>
            <button onClick={handleLogout}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-gray-500 hover:text-red-500 transition-colors">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Add button */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold text-gray-900">All Products</h2>
          <button onClick={openAdd}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-white font-semibold text-sm shadow hover:shadow-lg transition-all hover:scale-105"
            style={{ background: 'linear-gradient(135deg,hsl(350,70%,65%),hsl(20,90%,70%))' }}>
            <Plus className="w-4 h-4" /> Add Product
          </button>
        </div>

        {/* Product Table */}
        <div className="bg-white rounded-2xl shadow-soft overflow-hidden mb-8">
          {products.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <Package className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p>No products yet. Add your first one!</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Product</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden sm:table-cell">Collection</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Price</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden md:table-cell">Type</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden md:table-cell">Stock</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {products.map(p => (
                    <tr key={p.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <img src={p.images?.[0]} alt={p.name}
                            className="w-10 h-10 rounded-lg object-cover bg-gray-100 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-gray-900 line-clamp-1">{p.name}</p>
                            {p.badge && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded font-medium text-[hsl(350,70%,55%)] bg-[hsl(350,70%,65%)]/10">
                                {p.badge}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 hidden sm:table-cell">
                        <span className="text-xs text-gray-600">
                          {COLLECTIONS_LIST.find(c => c.id === p.collection)?.label}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm font-semibold text-gray-900">₹{p.price}</span>
                        {p.originalPrice && (
                          <span className="text-xs text-gray-400 line-through ml-1">₹{p.originalPrice}</span>
                        )}
                      </td>
                      <td className="py-3 px-4 hidden md:table-cell">
                        <span className="text-xs px-2 py-1 bg-gray-100 rounded-lg text-gray-600 capitalize">{p.type}</span>
                      </td>
                      <td className="py-3 px-4 hidden md:table-cell">
                        <span className={`text-xs px-2 py-1 rounded-lg font-medium ${p.inStock ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-500'}`}>
                          {p.inStock ? 'In Stock' : 'Out of Stock'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => openEdit(p)}
                            className="p-2 rounded-lg hover:bg-blue-50 text-gray-400 hover:text-blue-500 transition-colors">
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button onClick={() => setDeleteConfirm(p.id)}
                            className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── Add/Edit Form ── */}
        {formOpen && (
          <div ref={formRef} className="bg-white rounded-2xl shadow-soft p-6 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-gray-900">
                {editingId ? 'Edit Product' : 'Add New Product'}
              </h3>
              <button onClick={() => setFormOpen(false)}>
                <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
              </button>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              {/* Name */}
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Product Name *</label>
                <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. Sakura Pet Portrait Tee"
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(350,70%,65%)]/30" />
              </div>

              {/* Price */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Price (₹) *</label>
                <input type="number" value={form.price} onChange={e => setForm(p => ({ ...p, price: e.target.value }))}
                  placeholder="999"
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(350,70%,65%)]/30" />
              </div>

              {/* Original Price */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Original Price (₹) <span className="text-gray-400 font-normal">for discount</span></label>
                <input type="number" value={form.originalPrice} onChange={e => setForm(p => ({ ...p, originalPrice: e.target.value }))}
                  placeholder="1299 (optional)"
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(350,70%,65%)]/30" />
              </div>

              {/* Collection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Collection</label>
                <select value={form.collection} onChange={e => setForm(p => ({ ...p, collection: e.target.value }))}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none bg-white">
                  {COLLECTIONS_LIST.map(c => (
                    <option key={c.id} value={c.id}>{c.emoji} {c.label}</option>
                  ))}
                </select>
              </div>

              {/* Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Product Type</label>
                <select value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none bg-white">
                  {PRODUCT_TYPES.map(t => (
                    <option key={t.id} value={t.id}>{t.label}</option>
                  ))}
                </select>
              </div>

              {/* Badge */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Badge <span className="text-gray-400 font-normal">e.g. Bestseller, New</span></label>
                <input value={form.badge} onChange={e => setForm(p => ({ ...p, badge: e.target.value }))}
                  placeholder="Bestseller (optional)"
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(350,70%,65%)]/30" />
              </div>

              {/* Images */}
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Image URLs <span className="text-gray-400 font-normal">(add to /public/images/ folder)</span></label>
                {form.images.map((img, i) => (
                  <input key={i} value={img}
                    onChange={e => setForm(p => {
                      const imgs = [...p.images]; imgs[i] = e.target.value; return { ...p, images: imgs };
                    })}
                    placeholder={`/images/my-product-${i + 1}.png`}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(350,70%,65%)]/30 mb-2" />
                ))}
              </div>

              {/* Description */}
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
                <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                  rows={3} placeholder="Describe the product..."
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(350,70%,65%)]/30 resize-none" />
              </div>

              {/* Sizes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sizes Available</label>
                <div className="flex flex-wrap gap-2">
                  {SIZES_LIST.map(s => (
                    <button key={s} type="button" onClick={() => toggleArray('sizes', s)}
                      className={`w-10 h-10 rounded-lg border-2 text-sm font-semibold transition-all ${
                        form.sizes.includes(s)
                          ? 'border-[hsl(350,70%,65%)] bg-[hsl(350,70%,65%)]/10 text-[hsl(350,70%,55%)]'
                          : 'border-gray-200 text-gray-500 hover:border-gray-300'
                      }`}>{s}</button>
                  ))}
                </div>
              </div>

              {/* Colors */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Colors Available</label>
                <div className="flex flex-wrap gap-2">
                  {COLORS_LIST.map(c => (
                    <button key={c.name} type="button" onClick={() => toggleColor(c)}
                      title={c.name}
                      className={`w-8 h-8 rounded-full border-2 transition-all hover:scale-110 ${
                        (form.colors || []).some(fc => fc.name === c.name)
                          ? 'border-[hsl(350,70%,55%)] scale-110 shadow' : 'border-gray-200'
                      }`}
                      style={{ backgroundColor: c.hex }} />
                  ))}
                </div>
                {form.colors?.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1.5">{form.colors.map(c => c.name).join(', ')}</p>
                )}
              </div>

              {/* Occasions */}
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Occasions</label>
                <div className="flex flex-wrap gap-2">
                  {OCCASIONS_LIST.map(o => (
                    <button key={o.id} type="button" onClick={() => toggleArray('occasion', o.id)}
                      className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${
                        (form.occasion || []).includes(o.id)
                          ? 'border-[hsl(350,70%,65%)] bg-[hsl(350,70%,65%)]/10 text-[hsl(350,70%,55%)]'
                          : 'border-gray-200 text-gray-500 hover:border-gray-300'
                      }`}>{o.label}</button>
                  ))}
                </div>
              </div>

              {/* Tags */}
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Tags <span className="text-gray-400 font-normal">comma separated</span></label>
                <input value={form.tags} onChange={e => setForm(p => ({ ...p, tags: e.target.value }))}
                  placeholder="anime, pet, portrait, gift"
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(350,70%,65%)]/30" />
              </div>

              {/* Toggles */}
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.inStock}
                    onChange={e => setForm(p => ({ ...p, inStock: e.target.checked }))}
                    className="w-4 h-4 accent-[hsl(350,70%,65%)]" />
                  <span className="text-sm text-gray-700">In Stock</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.customisable}
                    onChange={e => setForm(p => ({ ...p, customisable: e.target.checked }))}
                    className="w-4 h-4 accent-[hsl(350,70%,65%)]" />
                  <span className="text-sm text-gray-700">Customisable</span>
                </label>
              </div>
            </div>

            <div className="flex gap-3 mt-6 pt-5 border-t border-gray-100">
              <button onClick={handleSave}
                className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-white font-semibold text-sm shadow hover:shadow-lg transition-all"
                style={{ background: 'linear-gradient(135deg,hsl(350,70%,65%),hsl(20,90%,70%))' }}>
                <Save className="w-4 h-4" />
                {editingId ? 'Save Changes' : 'Add Product'}
              </button>
              <button onClick={() => setFormOpen(false)}
                className="px-6 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 transition-colors">
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDeleteConfirm(null)} />
          <div className="relative bg-white rounded-2xl p-6 shadow-xl max-w-sm w-full">
            <h3 className="font-bold text-gray-900 mb-2">Delete Product?</h3>
            <p className="text-sm text-gray-500 mb-5">This action cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 py-2.5 rounded-xl bg-red-500 text-white font-semibold text-sm hover:bg-red-600 transition-colors">
                Delete
              </button>
              <button onClick={() => setDeleteConfirm(null)}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 transition-colors">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
